/**
 * Chart configuration utilities — axis normalization, default selection,
 * resolved config computation, and chart type switching helpers.
 *
 * The backend may send x_axis as a single object or an array. These helpers
 * normalize that and produce the resolved ChartConfig that ByodChart /
 * buildCategoricalSeries / buildProportionSeries consume (single x_axis).
 */

import {CHART_KIND_GROUPS} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';
import type {ChartKind} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';

import type {
  ChartAxis,
  ChartConfig,
  ChartSeries,
  ChartStyle,
} from 'src/types/byod';
import logger from 'src/utils/logger';

// ---------------------------------------------------------------------------
// Kind groups — sourced from the design system so BYOD stays in sync with
// UniversalChart's compatibility matrix.
// ---------------------------------------------------------------------------

const CATEGORICAL_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.CATEGORICAL.map((opt) => opt.key);
const PROPORTION_KIND_KEYS: ReadonlyArray<ChartKind> =
  CHART_KIND_GROUPS.PROPORTION.map((opt) => opt.key);
const FLOW_KIND_KEYS: ReadonlyArray<ChartKind> = CHART_KIND_GROUPS.FLOW.map(
  (opt) => opt.key,
);

export function isCategoricalKind(style: ChartStyle): boolean {
  return CATEGORICAL_KIND_KEYS.includes(style);
}

export function isProportionKind(style: ChartStyle): boolean {
  return PROPORTION_KIND_KEYS.includes(style);
}

export function isFlowKind(style: ChartStyle): boolean {
  return FLOW_KIND_KEYS.includes(style);
}

/**
 * Combo charts have a `y1_axis` / `y2_axis` split contract from the backend.
 * Today only `stackedBarLine` qualifies — checked explicitly so the combo
 * gate doesn't extend to DS additions we haven't audited.
 */
export function isComboKind(style: ChartStyle): boolean {
  return style === 'stackedBarLine';
}

export function getKindsInGroup(style: ChartStyle): ReadonlyArray<ChartStyle> {
  if (isProportionKind(style)) {
    return PROPORTION_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  if (isFlowKind(style)) {
    return FLOW_KIND_KEYS as ReadonlyArray<ChartStyle>;
  }
  if (isComboKind(style)) {
    return ['stackedBarLine'];
  }
  return CATEGORICAL_KIND_KEYS as ReadonlyArray<ChartStyle>;
}

// ---------------------------------------------------------------------------
// Non-modifiable chart styles — no axis customization or chart type switching
// ---------------------------------------------------------------------------

const NON_MODIFIABLE_STYLES: ReadonlySet<ChartStyle> = new Set([
  'funnel',
  'sankey',
]);

export function isChartModifiable(style: ChartStyle | undefined): boolean {
  if (!style) {
    return false;
  }
  return !NON_MODIFIABLE_STYLES.has(style);
}

// ---------------------------------------------------------------------------
// Axis field extraction (handles x_axis as single object or array)
// ---------------------------------------------------------------------------

/**
 * Extracts x-axis fields as an array. The backend may send x_axis as either a
 * single ChartAxis object or an array of ChartAxis objects.
 */
export function getXAxisFields(
  config: ChartConfig | null | undefined,
): ChartAxis[] {
  if (!config?.x_axis) {
    return [];
  }
  const xAxis = config.x_axis;
  if (Array.isArray(xAxis)) {
    return xAxis;
  }
  return [xAxis];
}

/** Returns y-axis fields (always an array, empty if missing). */
export function getYAxisFields(
  config: ChartConfig | null | undefined,
): ChartSeries[] {
  return config?.y_axis ?? [];
}

/**
 * Combo (`stackedBarLine`) only — primary (bar) field pool. May be absent
 * during the combo-shape rollout while the widget-definition endpoint
 * catches up; in that case `mergeComboPoolsFromPreview` folds in the pool
 * from the preview response. Always returns an array; callers do not need
 * to null-check.
 */
export function getY1AxisFields(
  config: ChartConfig | null | undefined,
): ChartSeries[] {
  return config?.y1_axis ?? [];
}

/**
 * Combo (`stackedBarLine`) only — secondary (line) field pool. May be
 * absent during the combo-shape rollout while the widget-definition
 * endpoint catches up; in that case `mergeComboPoolsFromPreview` folds in
 * the pool from the preview response. Always returns an array; callers do
 * not need to null-check.
 */
export function getY2AxisFields(
  config: ChartConfig | null | undefined,
): ChartSeries[] {
  return config?.y2_axis ?? [];
}

/**
 * A field is treated as percent when the backend tags it as `'percent'` or
 * `'rate'` OR when the label visibly conveys percent (e.g. "Rate (%)",
 * "Conversion %"). The label fallback covers definitions that pre-date the
 * `type` field. Used to drive axis ticks and tooltip suffixes.
 *
 * The label heuristic only fires when a `%` (or `(%)`) is preceded by a
 * non-digit boundary — so labels like "Top 10%" or "Discount 50%" remain
 * classified as ordinary numeric metrics, while "Conversion %" / "Rate (%)"
 * are picked up.
 */
export function isPercentField(field: ChartSeries): boolean {
  if (field.type === 'percent' || field.type === 'rate') {
    return true;
  }
  const label = (field.label ?? '').trim();
  return /\(\s*%\s*\)$/.test(label) || /(?:^|[^\d])\s*%$/.test(label);
}

/**
 * When the widget-definition endpoint lags behind preview during the
 * combo-shape rollout, the sidebar needs to see `y1_axis`/`y2_axis` from
 * the preview response. Whenever preview ships a combo split, we trust it
 * and fold both pools and the combo `default_chart_style` into the base
 * config — without gating on what the widget-definition endpoint said.
 *
 * For previews that don't ship a split, returns `defConfig` unchanged.
 */
export function mergeComboPoolsFromPreview(
  defConfig: ChartConfig | null | undefined,
  previewConfig: ChartConfig | null | undefined,
): ChartConfig | null {
  if (!defConfig) {
    return previewConfig ?? null;
  }
  const previewHasSplit =
    previewConfig?.y1_axis != null || previewConfig?.y2_axis != null;
  if (!previewHasSplit) {
    return defConfig;
  }
  return {
    ...defConfig,
    default_chart_style:
      previewConfig?.default_chart_style ?? defConfig.default_chart_style,
    y1_axis: previewConfig?.y1_axis ?? defConfig.y1_axis,
    y2_axis: previewConfig?.y2_axis ?? defConfig.y2_axis,
  };
}

// ---------------------------------------------------------------------------
// Chart type switching
// ---------------------------------------------------------------------------

/**
 * Returns the list of chart styles a user can switch to from the current style.
 * Switching is allowed only within the same kind group (categorical, proportion, flow).
 * Non-modifiable styles (funnel, sankey) are excluded from the result.
 */
export function getModifiableChartStyles(
  defaultStyle: ChartStyle | undefined,
): ChartStyle[] {
  if (!defaultStyle || !isChartModifiable(defaultStyle)) {
    return [];
  }
  // Flow charts (sankey) are non-modifiable, so this only applies to categorical/proportion.
  if (isFlowKind(defaultStyle)) {
    return [];
  }
  const group = getKindsInGroup(defaultStyle);
  return group.filter((s) => !NON_MODIFIABLE_STYLES.has(s));
}

// ---------------------------------------------------------------------------
// Default axis key selection
// ---------------------------------------------------------------------------

/**
 * Computes default axis key selections for a given chart config.
 *
 * - x-axis: first field (single-select in all modifiable charts)
 * - y-axis (primary): all fields. For combo this is `y1_axis` (bars, multi);
 *   for everything else, `y_axis`. Empty for donut.
 * - y2-axis (combo line): first `y2_axis` field only — y2 is single-select
 *   in the sidebar. Empty for non-combo.
 */
export function getDefaultAxisKeys(config: ChartConfig | null | undefined): {
  xAxisKeys: string[];
  yAxisKeys: string[];
  y2AxisKeys: string[];
} {
  const xFields = getXAxisFields(config);
  const isCombo = config?.default_chart_style === 'stackedBarLine';
  const yFields = isCombo ? getY1AxisFields(config) : getYAxisFields(config);
  const y2Fields = isCombo ? getY2AxisFields(config) : [];

  return {
    xAxisKeys: xFields.length > 0 ? [xFields[0].key] : [],
    yAxisKeys: yFields.map((f) => f.key),
    y2AxisKeys: y2Fields.length > 0 ? [y2Fields[0].key] : [],
  };
}

// ---------------------------------------------------------------------------
// Resolved chart config
// ---------------------------------------------------------------------------

/**
 * Filters a y-axis pool to the user's selection while preserving the backend
 * field order.
 *
 * - Empty `selectedKeys` → full pool ("nothing picked yet, show everything").
 * - Selection that matches at least one pool field → filtered subset.
 * - Selection that matches zero pool fields → full pool fallback, with a
 *   `logger.warn` so the stale-keys case is observable rather than silent.
 */
function filterYFields(
  pool: ChartSeries[],
  selectedKeys: string[],
): ChartSeries[] {
  if (selectedKeys.length === 0) {
    return pool;
  }
  const filtered = pool.filter((f) => selectedKeys.includes(f.key));
  if (filtered.length === 0) {
    logger.warn(
      'byod: y-axis selection matched no pool fields, falling back to full pool',
      {
        selectedKeys,
        poolKeys: pool.map((f) => f.key),
      },
    );
    return pool;
  }
  return filtered;
}

/**
 * Creates a resolved ChartConfig by filtering the full config down to only the
 * selected axis fields. For combo (`stackedBarLine`), the result carries
 * `y1_axis` + `y2_axis` (legacy `y_axis` stripped); for everything else, the
 * result carries `y_axis` and `selectedY2Keys` is ignored.
 *
 * Empty / non-matching selections fall back to the full source pool.
 */
export function resolveChartConfig(
  config: ChartConfig | null | undefined,
  selectedXKeys: string[],
  selectedYKeys: string[],
  selectedY2Keys: string[] = [],
): ChartConfig | null {
  if (!config) {
    return null;
  }

  const xFields = getXAxisFields(config);
  const selectedX =
    xFields.find((f) => selectedXKeys.includes(f.key)) ??
    xFields[0] ??
    undefined;

  if (config.default_chart_style === 'stackedBarLine') {
    // Drop legacy `y_axis` from the resolved output so downstream
    // consumers don't see both shapes simultaneously.
    const {y_axis: _legacyY, ...rest} = config;
    return {
      ...rest,
      x_axis: selectedX,
      y1_axis: filterYFields(getY1AxisFields(config), selectedYKeys),
      y2_axis: filterYFields(getY2AxisFields(config), selectedY2Keys),
    };
  }

  return {
    ...config,
    x_axis: selectedX,
    y_axis: filterYFields(getYAxisFields(config), selectedYKeys),
  };
}
